import { useQuery } from '@tanstack/react-query';
import { NavLink } from 'react-router';

import { fetchOpenAlertCount } from '../alerts/api';
import { Icon, type IconName } from '../ui/Icon';
import styles from './app.module.css';

interface NavItem {
  readonly to: string;
  readonly label: string;
  readonly icon: IconName;
}

const NAV_ITEMS: readonly NavItem[] = [
  { to: '/capturar', label: 'Captura', icon: 'camera' },
  { to: '/notas', label: 'Notas', icon: 'file-text' },
  { to: '/alertas', label: 'Alertas', icon: 'bell' },
  { to: '/ajustes', label: 'Ajustes', icon: 'settings' },
];

/**
 * The four signed-in destinations. One nav for every width - req. §46 requires the owner to
 * complete the normal workflow entirely from a phone, so nothing here is phone-only or
 * PC-only. Each item stacks its icon over its label, and the active one sits on a brand-tint
 * pill (`docs/design/visual-system.md` §4). "Alertas" carries an unread badge. Sits directly
 * under the app bar, inside the same sticky chrome, rather than pinned to the screen's bottom
 * edge.
 */
export function TopNav() {
  const alertCountQuery = useQuery({ queryKey: ['alerts', 'open-count'], queryFn: fetchOpenAlertCount });
  const openAlerts = alertCountQuery.data ?? 0;

  return (
    <nav className={styles.nav} aria-label="Navegación principal">
      {NAV_ITEMS.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          className={({ isActive }) =>
            [styles.navLink, isActive ? styles.navLinkActive : null].filter(Boolean).join(' ')
          }
        >
          {({ isActive }) => (
            <>
              {isActive ? <span className={styles.navPill} aria-hidden="true" /> : null}
              <Icon className={styles.navIcon} name={item.icon} size={20} strokeWidth={1.8} />
              <span className={styles.navLabel}>{item.label}</span>
              {item.to === '/alertas' && openAlerts > 0 ? (
                <span className={styles.navBadge}>{openAlerts}</span>
              ) : null}
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}
